import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  TextField,
} from "@mui/material";

const cellSx = {
  backgroundColor: "rgba(47, 53, 71, 0.9)",
  border: "1.5px solid #fff",
  color: "white",
  padding: "10px 8px",
};

const numberSx = {
  ...cellSx,
  textAlign: "left",
  fontWeight: 600,
};

const JustifiedWithdrawalIPSTable = ({
  declaredSalary = 0,
  christmasBonus = 0,
  accruedVacation = 0,
  proportionalVacation = 0,
  notice = 0,
  ipsSeverance = 0,
  total = 0,
  onValuesChange, // optional callback to notify parent on edits
}) => {
  // Editable fields stored as strings for controlled inputs
  const [editableAccruedVacation, setEditableAccruedVacation] = useState(
    String(accruedVacation)
  );
  const [editableProportionalVacation, setEditableProportionalVacation] =
    useState(String(proportionalVacation));
  const [editableNotice, setEditableNotice] = useState(String(notice));
  const [editableIpsSeverance, setEditableIpsSeverance] = useState(
    String(ipsSeverance)
  );

  // Sync prop changes to local state
  useEffect(() => setEditableAccruedVacation(String(accruedVacation)), [accruedVacation]);
  useEffect(() => setEditableProportionalVacation(String(proportionalVacation)), [proportionalVacation]);
  useEffect(() => setEditableNotice(String(notice)), [notice]);
  useEffect(() => setEditableIpsSeverance(String(ipsSeverance)), [ipsSeverance]);

  // Generic input change handler with validation
  const handleInputChange = (setter, name) => (event) => {
    const val = event.target.value;
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      setter(val);
      if (typeof onValuesChange === "function") {
        onValuesChange({
          accruedVacation:
            name === "accruedVacation" ? Number(val) || 0 : Number(editableAccruedVacation) || 0,
          proportionalVacation:
            name === "proportionalVacation" ? Number(val) || 0 : Number(editableProportionalVacation) || 0,
          notice: name === "notice" ? Number(val) || 0 : Number(editableNotice) || 0,
          ipsSeverance:
            name === "ipsSeverance" ? Number(val) || 0 : Number(editableIpsSeverance) || 0,
        });
      }
    }
  };

  // Calculate total dynamically with editable fields and fixed props
  const correctedTotal =
    (Number(declaredSalary) || 0) +
    (Number(christmasBonus) || 0) +
    (Number(editableAccruedVacation) || 0) +
    (Number(editableProportionalVacation) || 0) +
    (Number(editableNotice) || 0) +
    (Number(editableIpsSeverance) || 0);

  // Format number with 2 fixed decimals for display
  const formatNum = (num) =>
    num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // Table rows where editable fields get a TextField
  const rows = [
    ["Salario Declarado(Mes)", formatNum(declaredSalary), "Declarado a IPS, durante el último mes"],
    ["Aguinaldo", formatNum(christmasBonus), "Aguinaldo del año en curso"],
    [
      "Vacaciones Causadas",
      <TextField
        value={editableAccruedVacation}
        onChange={handleInputChange(setEditableAccruedVacation, "accruedVacation")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "Si tiene más de 1 año de servicio",
    ],
    [
      "Vacaciones Proporcionales",
      <TextField
        value={editableProportionalVacation}
        onChange={handleInputChange(setEditableProportionalVacation, "proportionalVacation")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "Si es menos de 1 año (proporcional)",
    ],
    [
      "Preaviso",
      <TextField
        value={editableNotice}
        onChange={handleInputChange(setEditableNotice, "notice")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "Establecer en 0 si no corresponde",
    ],
    [
      "IPS Indemnización",
      <TextField
        value={editableIpsSeverance}
        onChange={handleInputChange(setEditableIpsSeverance, "ipsSeverance")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "Basado en el promedio de los últimos 6 meses declarados",
    ],
  ];

  return (
    <Box mt={4}>
      <Typography variant="h6" mb={2} fontWeight="bold" color="white">
        Retiro Justificado Art. 84 – IPS
      </Typography>
      <TableContainer component={Paper} sx={{ background: "rgba(47,53,71,0.9)" }}>
        <Table>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={cellSx}>{row[0]}</TableCell>
                <TableCell sx={numberSx}>{row[1]}</TableCell>
                <TableCell sx={cellSx}>{row[2]}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Total a Pagar</TableCell>
              <TableCell
                colSpan={2}
                sx={{
                  ...numberSx,
                  background: "rgba(34,189,110,0.15)",
                  color: "#3afa60",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: 20,
                }}
              >
                {correctedTotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default JustifiedWithdrawalIPSTable;
